/**
 * Remembers which batch a staff member last took attendance for, so the
 * dashboard board reopens on it instead of always snapping back to the first
 * batch. Purely a convenience cache — never a permission boundary. The batch id
 * is re-validated against the batches the server actually returns before it is
 * used, so a stale or hand-edited value degrades to "first batch", and RLS still
 * gates every read.
 *
 * Scoped by institute: one person can be staff at more than one institute, and
 * a batch id from institute A must never be pre-selected while they are looking
 * at institute B.
 *
 * Deliberately NOT scoped by user id. Reading the signed-in user would mean
 * importing the auth context from `app/` into a `shared/` module, inverting the
 * layer direction. User isolation comes from `clearSelectedBatches()` on
 * sign-out instead. The residual case — someone closes the browser without
 * signing out and a colleague signs in — surfaces a batch of the *same*
 * institute that the second person already has access to, so the cost is a
 * surprising default, not exposure. RLS gates the data either way.
 *
 * Every access is wrapped in try/catch so a missing or blocked localStorage
 * (private mode, SSR, a test shim) is harmless — the caller just falls back.
 */

const PREFIX = 'selected_batch:';
const VIEW_PREFIX = 'attendance_view:';

function storageKey(instituteId: string): string {
  return `${PREFIX}${instituteId}`;
}

function viewKey(instituteId: string): string {
  return `${VIEW_PREFIX}${instituteId}`;
}

/**
 * Which span the attendance board was last opened on ('day' | 'week' |
 * 'month'). Same convenience-cache contract as the batch above: the caller
 * validates the string, so anything unexpected degrades to the default.
 */
export function readAttendanceView(instituteId: string): string | null {
  try {
    return localStorage.getItem(viewKey(instituteId));
  } catch {
    return null;
  }
}

export function writeAttendanceView(instituteId: string, view: string): void {
  try {
    localStorage.setItem(viewKey(instituteId), view);
  } catch {
    /* storage unavailable — the choice still holds for this session */
  }
}

export function readSelectedBatch(instituteId: string): string | null {
  try {
    return localStorage.getItem(storageKey(instituteId));
  } catch {
    return null;
  }
}

export function writeSelectedBatch(instituteId: string, batchId: string): void {
  try {
    localStorage.setItem(storageKey(instituteId), batchId);
  } catch {
    /* storage unavailable — selection still works for this session */
  }
}

/**
 * Drop every remembered batch, for every institute. Called on sign-out:
 * institute machines are shared, and the next person to log in must not inherit
 * the previous teacher's batch.
 */
export function clearSelectedBatches(): void {
  try {
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key !== null && (key.startsWith(PREFIX) || key.startsWith(VIEW_PREFIX))) doomed.push(key);
    }
    doomed.forEach((key) => {
      localStorage.removeItem(key);
    });
  } catch {
    /* storage unavailable — nothing was persisted to begin with */
  }
}
